"use client";

import { useState, useRef, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import type { EntityId, Group } from "@theta/api-client";
import { errorText } from "@/lib/errors";
import {
  useCreateEvent,
  useUpdateEvent,
  type CreateEventInput,
  type ThetaEvent,
  type UpdateEventInput,
} from "@/lib/hooks/useEvents";
import { useUpload } from "@/lib/hooks/useUpload";
import { useMyGroups } from "@/lib/hooks/useMyGroups";
import { ImageCropper, croppedFile } from "./ImageCropper";
import { RichTextInput } from "./RichTextInput";
import { SubmitTooltip } from "./SubmitShortcut";
import { ConfirmDialog } from "./ui/ConfirmDialog";
import { Dialog, DialogTitle } from "./ui/Dialog";

/**
 * Create is a POST, edit is a PATCH (§1.7), not one function taking a full body
 * either way. On edit only what changed is sent: an absent key is untouched and
 * an explicit `null` clears, so re-saving no longer rewrites every field with
 * whatever the form happened to hold.
 *
 * `Idempotency-Key` is required on create (D13 names `event`) and the client's
 * middleware generates it, so a double-tap is one event rather than two.
 */

/** Everything the form holds that a save sends — see `initial` and `seedForm`. */
interface FormValues {
  title: string;
  description: string;
  location: string;
  locationUrl: string;
  startsAt: string;
  endsAt: string;
  capacity: string;
  groupIds: Group["id"][];
  isPrivate: boolean;
  coverUrl: string;
}

function toLocalDatetime(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  // A draft can arrive from a URL somebody typed, so an unparseable date is
  // reachable here even though the tool that writes these validates first.
  // Empty is the honest seed for "no date"; "NaN-NaN-NaN" is not.
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * A proposal to fill the form with — from the bot's `propose_event` tool, which
 * hands a member a link rather than creating anything (see
 * `backend/src/modules/llm-bot/tools/propose-event.ts` for why). It is a SEED,
 * not a value: the person editing owns every field from the moment it opens,
 * and `isDirty` measures against what they were shown, so a draft they change
 * their mind about warns on close exactly like anything else they typed.
 */
export interface EventDraft {
  title?: string;
  description?: string;
  location?: string;
  startsAt?: string;
  endsAt?: string;
}

export function EventComposer({
  open,
  onClose,
  defaultGroupId,
  editEvent,
  draft,
}: {
  open: boolean;
  onClose: () => void;
  defaultGroupId?: Group["id"];
  editEvent?: ThetaEvent;
  draft?: EventDraft;
}) {
  const upload = useUpload();
  const { data: myGroups = [] } = useMyGroups();

  /**
   * What the form opens as. Derived from a live `editEvent` — the page it comes
   * from polls — so it drifts under a form nobody is touching. `isDirty`
   * measures against `baseline` below, the snapshot the form was seeded with.
   */
  // `editEvent` wins over `draft`: editing a real event is never seeded from a
  // proposal, and nothing should be able to overwrite a live row's own values
  // with a link's query string.
  const initial: FormValues = {
    title: editEvent?.title ?? draft?.title ?? "",
    description: editEvent?.description ?? draft?.description ?? "",
    location: editEvent?.location ?? draft?.location ?? "",
    locationUrl: editEvent?.locationUrl ?? "",
    startsAt: toLocalDatetime(editEvent?.startsAt ?? draft?.startsAt),
    endsAt: toLocalDatetime(editEvent?.endsAt ?? draft?.endsAt),
    capacity: editEvent?.capacity?.toString() ?? "",
    groupIds: editEvent?.groups.map((g) => g.id) ?? (defaultGroupId ? [defaultGroupId] : []),
    isPrivate: editEvent?.isPrivate ?? false,
    coverUrl: editEvent?.coverUrl ?? "",
  };

  /**
   * One object, not ten `useState`s: the fields are seeded, compared and reset
   * together, and each of those was a hand-written list of the same ten names —
   * which is how both of this file's dirty-state bugs got in. `FormValues` is
   * the only place they are enumerated.
   */
  const [form, setForm] = useState<FormValues>(initial);
  const set = useCallback((patch: Partial<FormValues>) => setForm((f) => ({ ...f, ...patch })), []);

  const [coverUploadId, setCoverUploadId] = useState<EntityId<"upload"> | undefined>(undefined);
  /**
   * The cropped cover, held until save — see `coverScopeGroupId` below. The
   * preview is an object URL over this same blob, revoked on unmount.
   */
  const [coverFile, setCoverFile] = useState<File | null>(null);
  /** Which group's `cover` scope the held upload was minted against. */
  const [coverScopeGroupId, setCoverScopeGroupId] = useState<Group["id"] | undefined>(undefined);
  const previewUrl = useRef<string | null>(null);
  const [cropperImage, setCropperImage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** The "you are about to lose this" gate — see `requestClose` below. */
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const coverInputRef = useRef<HTMLInputElement>(null);

  /**
   * What the form was seeded with — a snapshot, deliberately not `initial`.
   *
   * The composer is never unmounted (every caller renders it with `open` as a
   * prop), so its state survives a close, and without a baseline there is no
   * telling "opened and closed again" from "half an hour of typing, dismissed by
   * a stray click on the backdrop" — the loss OSO-404 reports.
   *
   * Not `initial`, because `initial` follows the server: a non-public cover comes
   * back re-signed every time the `?exp=&sig=` window rolls
   * (`MEDIA_SIGNED_WINDOW_SECONDS`, an hour), so diffing live values against form
   * state would raise "Kassér dine ændringer?" on a dialog nobody typed in. What
   * the user changed is measured against what they were shown.
   */
  const [baseline, setBaseline] = useState(initial);

  useEffect(
    () => () => {
      if (previewUrl.current) URL.revokeObjectURL(previewUrl.current);
    },
    [],
  );

  /**
   * Seed the form and the baseline together — the composer outlives its own
   * close, so every opening starts here.
   *
   * The held cover is dropped and its object URL revoked: after a save it is a
   * `blob:` preview of a picture the server now has its own (differently
   * addressed) copy of, and keeping it would both show the stale blob and make
   * `coverFile !== null` say "dirty" for the rest of the session.
   */
  const seedForm = useCallback((from: FormValues) => {
    setForm(from);
    setBaseline(from);
    setCoverFile(null);
    setCoverUploadId(undefined);
    setCoverScopeGroupId(undefined);
    if (previewUrl.current) {
      URL.revokeObjectURL(previewUrl.current);
      previewUrl.current = null;
    }
    setCropperImage(null);
    setError(null);
  }, []);

  /** The freshest `initial`, for the callbacks that outlive their render. */
  const initialRef = useRef(initial);
  useEffect(() => {
    initialRef.current = initial;
  });

  /**
   * Every opening starts from what the server now says — on the transition into
   * `open` only, never while open: re-seeding a form someone is typing in would
   * take their words away on the next poll. Between openings there is nothing to
   * protect, and re-seeding is what keeps "Rediger begivenhed" from re-opening
   * on the previous session's edits.
   */
  const wasOpen = useRef(open);
  useEffect(() => {
    if (open && !wasOpen.current) seedForm(initialRef.current);
    wasOpen.current = open;
  }, [open, seedForm]);

  const isEdit = !!editEvent;
  const createMutation = useCreateEvent();
  const updateMutation = useUpdateEvent(editEvent?.id ?? 0);
  const saveMutation = isEdit ? updateMutation : createMutation;

  /**
   * The group whose `cover` scope the upload declares. `myGroups` is the
   * viewer's own list, so a group found in it is one they hold a membership
   * in — the write side of the same ACL.
   */
  const scopeGroupId =
    form.groupIds.find((id) => myGroups.some((g) => g.id === id)) ?? form.groupIds[0];

  /**
   * What "Offentlig begivenhed" can honestly promise.
   *
   * The switch clears `is_private`, which is half of what makes a link work in a
   * stranger's hands: `eventVisible` for an anonymous viewer is `NOT is_private
   * AND hosted by a group that viewer can see`, and a private or secret group is
   * not one. So for an event hosted only in a private group, turning this on
   * changes nothing observable — no "Offentlig" badge (which follows
   * `isPubliclyVisible`), and a share button still saying only members can open
   * the link. The old unconditional "Synlig for alle — også ikke-medlemmer" was
   * the backend's answer contradicted where the author makes the decision.
   *
   * `myGroups` is `Group[]`, which carries `privacyLevel`, so this reads off data
   * already on the page — no second fetch, no widened endpoint.
   *
   * Three states, because "not public" and "we cannot tell" are different claims.
   * The checkbox list only offers `myGroups`, but an edit seeds `groupIds` from
   * the event, which may host it in a group the viewer is not a member of; that
   * group's privacy is unknowable here, so an unresolved id keeps the softened
   * wording rather than asserting a private-only event.
   */
  const hostingGroups = form.groupIds.map((id) => myGroups.find((g) => g.id === id));
  const someGroupIsPublic = hostingGroups.some(
    (g) => g?.privacyLevel === "public_group",
  );
  const everyGroupIsKnown = hostingGroups.length > 0 && hostingGroups.every(Boolean);
  const publicHelperText = someGroupIsPublic
    ? "Synlig for alle — også ikke-medlemmer"
    : everyGroupIsKnown
      ? "Gruppen er privat, så begivenheden er kun synlig for medlemmer"
      : "Synlig for alle, hvis gruppen er offentlig";

  /**
   * A mutation that can fail with no way to say so is how the cover bug stayed
   * invisible: with no `isError` and no `onError` anywhere in this file, the
   * button flashed "Gemmer...", the modal stayed open, and nothing was created.
   * The server names its refusals; this prints the one it sent.
   */
  function reportSaveError(err: unknown) {
    setError(errorText(err, "Begivenheden kunne ikke gemmes. Prøv igen."));
  }

  /**
   * The cover is scoped to a group the event is in, and uploaded at save.
   *
   * This used to upload the cropped cover with `scope: {type:"avatar"}` the
   * moment the cropper closed, on the note that "`UploadScope` has no variant
   * for an event cover". It has: the frozen vocabulary spells the `cover` arm
   * "a group's or an event's cover image, in this group"
   * (modules/uploads/scope.ts). `POST`/`PATCH /events` put every `coverUploadId`
   * through `resolveCover` -> `scopeAdmits`, which refuses any row whose
   * `scopeType` is not `cover` naming one of the event's groups — so an `avatar`
   * row was `422 attachmentNotOwned` on every event with a cover, create and
   * edit alike, accusing the author of using somebody else's file.
   *
   * The upload moved to save because the scope names a group, and which group
   * only becomes true at submit: a cover may be cropped before any group is
   * ticked, and the ticks may change afterwards. The blob is held and uploaded
   * here, against the selection actually being saved.
   *
   * Which group: the first selected one the viewer is a member of.
   * `canWriteToScope("cover", id)` demands an active membership, and on an edit
   * the event's groups may include one the editor does not belong to;
   * `resolveCover` admits any of the event's groups, so naming one we can
   * legally write to is both sufficient and the only option that works.
   */
  async function save() {
    setError(null);

    let coverId = coverUploadId;
    if (coverFile) {
      if (scopeGroupId === undefined) {
        setError("Vælg en gruppe, før du tilføjer et coverbillede.");
        return;
      }
      /**
       * The blob is KEPT after a successful upload, so a save that fails for
       * some other reason and is retried against a DIFFERENT group set can
       * mint a scope the new set admits. An upload already scoped to a group
       * that is still selected is reused — `resolveCover` accepts any of the
       * event's groups — so a retry is not a second file.
       */
      const scopeStillAdmitted =
        coverId !== undefined &&
        coverScopeGroupId !== undefined &&
        form.groupIds.includes(coverScopeGroupId);
      if (!scopeStillAdmitted) {
        try {
          const uploaded = await upload.mutateAsync({
            file: coverFile,
            scope: { type: "cover", id: scopeGroupId },
          });
          coverId = uploaded.id;
          setCoverUploadId(uploaded.id);
          setCoverScopeGroupId(scopeGroupId);
        } catch (err) {
          // `uploadFile` already re-throws the SERVER's message when it sent one
          // (`fileTooLarge`, `unsupportedFileType`), as a plain Error.
          setError(
            err instanceof Error && err.message
              ? err.message
              : "Coverbilledet kunne ikke uploades. Prøv igen.",
          );
          return;
        }
      }
    }

    const fields = {
      title: form.title,
      description: form.description || null,
      location: form.location || null,
      locationUrl: form.locationUrl || null,
      startsAt: new Date(form.startsAt).toISOString(),
      endsAt: form.endsAt ? new Date(form.endsAt).toISOString() : null,
      capacity: form.capacity ? Number.parseInt(form.capacity, 10) : null,
      ...(coverId !== undefined ? { coverUploadId: coverId } : {}),
      isPrivate: form.isPrivate,
      groupIds: form.groupIds,
    } satisfies CreateEventInput;

    /**
     * BOTH PATHS RESET ON THE WAY OUT — one callback, so neither can forget.
     * The composer is never unmounted, so without this the next opening holds
     * the event that was just saved: `isDirty` stops the author on their way
     * out of a form they never touched, over a preview still showing the
     * `blob:` the server has since replaced with its own URL. The edit path
     * used to skip it, and that is exactly what it looked like.
     *
     * `initialRef`, not `initial`: this closure was made before the save, and
     * the values worth seeding from are the ones the re-fetch has landed by
     * the time it runs.
     */
    const onSaved = () => {
      seedForm(initialRef.current);
      onClose();
    };

    if (!isEdit) {
      createMutation.mutate(fields, { onSuccess: onSaved, onError: reportSaveError });
      return;
    }

    /**
     * §1.7: NAME ONLY WHAT CHANGED. `PATCH` leaves an absent key alone, so a
     * re-save no longer rewrites every field — which matters because only a
     * title, location or time change wakes the attendees, and the old PUT sent
     * all three every time.
     *
     * `groupIds` is deliberately included only when the SET differs: sending
     * it re-checks membership on every named group (E4).
     */
    const patch: UpdateEventInput = {};
    if (fields.title !== editEvent.title) patch.title = fields.title;
    if (fields.description !== editEvent.description) patch.description = fields.description;
    if (fields.location !== editEvent.location) patch.location = fields.location;
    if (fields.locationUrl !== editEvent.locationUrl) patch.locationUrl = fields.locationUrl;
    if (fields.startsAt !== editEvent.startsAt) patch.startsAt = fields.startsAt;
    if (fields.endsAt !== editEvent.endsAt) patch.endsAt = fields.endsAt;
    if (fields.capacity !== editEvent.capacity) patch.capacity = fields.capacity;
    if (fields.isPrivate !== editEvent.isPrivate) patch.isPrivate = fields.isPrivate;
    if (coverId !== undefined) patch.coverUploadId = coverId;
    const before = editEvent.groups.map((g) => g.id);
    if (
      before.length !== form.groupIds.length ||
      before.some((id) => !form.groupIds.includes(id))
    ) {
      patch.groupIds = form.groupIds;
    }

    updateMutation.mutate(patch, { onSuccess: onSaved, onError: reportSaveError });
  }

  const handleCoverSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    setCropperImage(url);
    e.target.value = "";
  };

  /**
   * The crop is kept, not sent. The scope the file has to declare names the
   * group the event will belong to, which is not settled until `save()`.
   */
  const handleCroppedCover = useCallback((blob: Blob) => {
    setCropperImage(null);
    setError(null);
    const file = croppedFile(blob, "event-cover");
    if (previewUrl.current) URL.revokeObjectURL(previewUrl.current);
    const preview = URL.createObjectURL(blob);
    previewUrl.current = preview;
    setCoverFile(file);
    setCoverUploadId(undefined);
    setCoverScopeGroupId(undefined);
    setForm((f) => ({ ...f, coverUrl: preview }));
  }, []);

  /**
   * A DISMISSAL THAT WOULD LOSE WORK ASKS FIRST (OSO-404)
   *
   * The backdrop and Escape both dismiss, and both used to do it instantly —
   * "if i create an event, but accidentally click outside the modal instead of
   * on the submit button, all my work is lost". Every exit now goes through
   * `requestClose`, which is silent for an untouched form and stops for a
   * touched one; `ConfirmDialog` is the app's one way to ask, because
   * `window.confirm` is ratcheted out (see ratchets.json).
   *
   * NOT `useDraft`. That persists ONE string, and this form is nine fields, a
   * group selection and a held image blob — a draft of it would be a second
   * serialisation of the whole composer, and what is being protected against
   * is a mis-click, not a navigation away.
   */
  const isDirty =
    coverFile !== null ||
    (Object.keys(form) as (keyof FormValues)[]).some((key) =>
      // `groupIds` by SET, not by identity: unticking a group and ticking it
      // straight back on re-appends it in a different order, and that is not
      // an edit. Every other field is a scalar.
      key === "groupIds"
        ? form.groupIds.length !== baseline.groupIds.length ||
          form.groupIds.some((id) => !baseline.groupIds.includes(id))
        : form[key] !== baseline[key],
    );

  function requestClose() {
    if (isDirty) {
      setConfirmDiscard(true);
      return;
    }
    onClose();
  }

  function discardAndClose() {
    setConfirmDiscard(false);
    seedForm(initial);
    onClose();
  }

  function toggleGroup(id: Group["id"]) {
    setForm((f) => ({
      ...f,
      groupIds: f.groupIds.includes(id) ? f.groupIds.filter((g) => g !== id) : [...f.groupIds, id],
    }));
  }

  const canSave = form.title.trim() && form.startsAt && form.groupIds.length > 0;

  if (!open) return null;

  return createPortal(
    <>
      {/* The shared `Dialog`, which is what brings the backdrop, Escape,
          `role="dialog"` and the focus trap (OSO-402). Both of its dismissals
          land on `requestClose`. */}
      <Dialog
        open
        onClose={requestClose}
        className="max-w-lg max-h-[90vh] flex flex-col"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-border shrink-0">
          <DialogTitle className="font-bold">
            {isEdit ? "Rediger begivenhed" : "Opret begivenhed"}
          </DialogTitle>
          <button
            onClick={requestClose}
            aria-label="Luk"
            className="p-2 text-subtle-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="p-4 space-y-4 overflow-y-auto min-h-0">
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">
              Coverbillede
            </label>
            <div
              className="relative h-32 rounded-lg overflow-hidden border border-dashed border-border-strong cursor-pointer hover:border-indigo-400 transition-colors"
              onClick={() => coverInputRef.current?.click()}
            >
              {form.coverUrl ? (
                <img src={form.coverUrl} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-subtle-foreground">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                    <circle cx="8.5" cy="8.5" r="1.5" />
                    <polyline points="21 15 16 10 5 21" />
                  </svg>
                  <span className="text-xs mt-1">Tilf&oslash;j cover</span>
                </div>
              )}
              {upload.isPending && (
                <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                  <div className="text-white text-sm font-medium">Uploader...</div>
                </div>
              )}
            </div>
            <input
              ref={coverInputRef}
              type="file"
              accept="image/*"
              onChange={handleCoverSelect}
              className="sr-only"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">
              Titel *
            </label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => set({ title: e.target.value })}
              placeholder="Hvad hedder begivenheden?"
              className="w-full rounded-lg border border-border-strong bg-surface text-foreground px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">
                Start *
              </label>
              <input
                type="datetime-local"
                value={form.startsAt}
                onChange={(e) => set({ startsAt: e.target.value })}
                className="w-full rounded-lg border border-border-strong bg-surface text-foreground px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">
                Slut
              </label>
              <input
                type="datetime-local"
                value={form.endsAt}
                onChange={(e) => set({ endsAt: e.target.value })}
                className="w-full rounded-lg border border-border-strong bg-surface text-foreground px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">
              Sted
            </label>
            <input
              type="text"
              value={form.location}
              onChange={(e) => set({ location: e.target.value })}
              placeholder="Tilf&oslash;j et sted"
              className="w-full rounded-lg border border-border-strong bg-surface text-foreground px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">
              Link til sted (valgfrit)
            </label>
            <input
              type="url"
              value={form.locationUrl}
              onChange={(e) => set({ locationUrl: e.target.value })}
              placeholder="https://maps.google.com/..."
              className="w-full rounded-lg border border-border-strong bg-surface text-foreground px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">
              Beskrivelse
            </label>
            <RichTextInput
              value={form.description}
              ariaLabel="Beskrivelse af begivenheden"
              onChange={(v) => set({ description: v })}
              multiline
              placeholder="Fortæl mere om begivenheden..."
              /* The same group the cover is scoped to: `GET /users?inGroup=`
                 answers for a group the viewer can read the members of, and on
                 an EDIT the event's first group may not be one of theirs. */
              groupId={scopeGroupId}
              /* An event DESCRIPTION is not a group post: no producer would
                 ever fan an @alle out from it, so the row's promise would be
                 false here (OSO-681). The discussion's comment composer
                 (EventDiscussion) keeps it — those are real group posts. */
              hideMentionAll
              onSubmit={() => {
                if (canSave && !saveMutation.isPending && !upload.isPending) void save();
              }}
              /* `text-sm` ON PURPOSE, not `text-content` (OSO-629): the rule is
                 that a composer matches what its destination RENDERS, and the
                 event page draws the description at `text-sm`
                 (EventDetailClient.tsx, "Detaljer") — converting only this
                 field would recreate the very mismatch OSO-629 removed.
                 Exempted, with this reason, in contentTextSize.test.tsx. */
              className="w-full rounded-lg border border-border-strong bg-surface text-foreground px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-vertical"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">
              Maks. antal deltagere (valgfrit)
            </label>
            <input
              type="number"
              min="1"
              value={form.capacity}
              onChange={(e) => set({ capacity: e.target.value })}
              placeholder="Ubegrænset"
              className="w-full rounded-lg border border-border-strong bg-surface text-foreground px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <label className="block text-xs font-medium text-muted-foreground">
                Offentlig begivenhed
              </label>
              <p className="text-xs text-subtle-foreground">
                {publicHelperText}
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={!form.isPrivate}
              onClick={() => setForm((f) => ({ ...f, isPrivate: !f.isPrivate }))}
              className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                !form.isPrivate ? "bg-indigo-600" : "bg-gray-300 dark:bg-gray-600"
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform transition-transform ${
                  !form.isPrivate ? "translate-x-5" : "translate-x-0"
                }`}
              />
            </button>
          </div>

          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">
              Grupper *
            </label>
            <p className="text-xs text-subtle-foreground mb-2">
              Vælg hvilke grupper begivenheden tilhører
            </p>
            <div className="space-y-1.5 max-h-40 overflow-y-auto">
              {myGroups.map((g) => (
                <label
                  key={g.id}
                  className={`flex items-center gap-2.5 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                    form.groupIds.includes(g.id)
                      ? "border-indigo-300 dark:border-indigo-700 bg-primary-muted"
                      : "border-border hover:bg-surface-2"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={form.groupIds.includes(g.id)}
                    onChange={() => toggleGroup(g.id)}
                    className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="text-sm text-foreground">
                    {g.name}
                  </span>
                </label>
              ))}
              {myGroups.length === 0 && (
                <p className="text-sm text-subtle-foreground py-2">
                  Du er ikke medlem af nogen grupper endnu.
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="px-4 py-3 border-t border-border shrink-0">
          {error && (
            <p role="alert" className="mb-2 text-sm text-destructive">
              {error}
            </p>
          )}
          <div className="flex gap-2 justify-end">
            <button
              type="button"
              onClick={requestClose}
              className="px-4 py-2 text-sm font-medium text-foreground bg-surface rounded-lg border border-border hover:bg-surface-2 transition-colors"
            >
              Annuller
            </button>
            <SubmitTooltip label={isEdit ? "Gem ændringer" : "Opret begivenhed"} className="relative">
              <button
                type="button"
                onClick={() => void save()}
                disabled={!canSave || saveMutation.isPending || upload.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
              >
                {upload.isPending
                  ? "Uploader..."
                  : saveMutation.isPending
                    ? "Gemmer..."
                    : isEdit
                      ? "Gem ændringer"
                      : "Opret begivenhed"}
              </button>
            </SubmitTooltip>
          </div>
        </div>
        {/* Image cropper. Inside the panel, not beside it: it is its own
            `fixed inset-0` overlay either way, and a DOM child is one the
            dialog's focus trap counts as "inside" — beside it, Tab would be
            pulled off the crop buttons and back into the form behind. */}
        {cropperImage && (
          <ImageCropper
            src={cropperImage}
            aspect={16 / 9}
            onCrop={handleCroppedCover}
            onCancel={() => setCropperImage(null)}
          />
        )}
      </Dialog>

      <ConfirmDialog
        open={confirmDiscard}
        title={isEdit ? "Kassér dine ændringer?" : "Kassér begivenheden?"}
        description={
          isEdit
            ? "Dine ændringer er ikke gemt endnu. Hvis du lukker nu, mister du dem."
            : "Begivenheden er ikke oprettet endnu. Hvis du lukker nu, mister du det, du har udfyldt."
        }
        confirmLabel="Kassér"
        cancelLabel="Fortsæt redigering"
        onConfirm={discardAndClose}
        onCancel={() => setConfirmDiscard(false)}
      />
    </>,
    document.body
  );
}
